import { useMemo } from 'react';
import { Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useUserData } from '@shared/hooks/useUser';
import { UserModel } from '../../models/User';
import { UserListRow } from './UserListRow';

interface UsersListProps {
  users: UserModel[];
  rolesMap: Record<number, string>;
  onRefetch: () => void;
}

export function UsersList({ users, rolesMap, onRefetch }: UsersListProps) {
  const { t } = useTranslation('usersModule');
  const { data: userData } = useUserData();

  const currentUserEmail = userData?.user?.email?.toLowerCase();

  const rowData = useMemo(
    () =>
      users.map((user, index) => {
        const displayName = user.name?.trim() || user.email || '-';
        const isCurrentUser = !!currentUserEmail && !!user.email && currentUserEmail === user.email.toLowerCase();

        return {
          user,
          displayName,
          roleName: rolesMap[user.current_role_id] || '-',
          isLast: index === users.length - 1,
          showActions: !!userData?.user && !isCurrentUser,
        };
      }),
    [users, rolesMap, currentUserEmail, userData?.user],
  );

  return (
    <div className="flex min-h-0 flex-1 w-full flex-col overflow-hidden rounded-lg border border-border bg-background">
      <div className="grid shrink-0 grid-cols-[1fr_auto_2.5rem] items-center gap-3 border-b border-border bg-background px-4 py-3">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <Users className="h-4 w-4 shrink-0 text-icons" />
          <span>{t('list.member')}</span>
        </div>
        <span className="text-xs font-medium text-muted-foreground sm:text-sm">{t('list.role')}</span>
        <span className="sr-only">{t('table.actions.menu')}</span>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-px">
        {rowData.map(({ user, displayName, roleName, isLast, showActions }) => (
          <UserListRow
            key={user.id}
            user={user}
            roleName={roleName}
            displayName={displayName}
            isLast={isLast}
            showActions={showActions}
            onRefetch={onRefetch}
          />
        ))}
      </div>
    </div>
  );
}
